/* iter376 — NIS History Chart for Diary
 *
 * Visualises per-meal NIS% across the day as a small SVG line chart with
 * status-colored dots. Tightly coupled to the `/api/meals/today` response
 * `nis_by_meal` + `nis_daily` payload (added in iter376).
 *
 * Why this exists: prior to iter375 the Diary showed a SINGLE daily NIS
 * number that inflated with each added meal (linear Holt cross-track bug).
 * After iter375 NIS is correctly per-meal density. This component surfaces
 * that per-meal granularity to the patient: peak vs average + temporal
 * pattern across breakfast/lunch/dinner/snacks.
 *
 * Used by: pages/DiaryPage.jsx (under the existing macro totals card)
 */
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Activity, TrendingUp, Info } from "lucide-react";
import { useDashboardMode } from "@/dashboard/DashboardModeContext";  // iter566

const MEAL_ORDER = ["breakfast", "lunch", "snack", "dinner"];
const MEAL_LABEL_BG = {
  breakfast: "Закуска",
  lunch: "Обяд",
  snack: "Междинни",
  dinner: "Вечеря",
};
const MEAL_LABEL_EN = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  snack: "Snacks",
  dinner: "Dinner",
};

function tierColor(pct) {
  if (pct < 25) return "#10B981";       // emerald — Low
  if (pct < 50) return "#84CC16";       // lime — Moderate
  if (pct < 70) return "#F59E0B";       // amber — Medium
  if (pct < 85) return "#F97316";       // orange — High
  return "#EF4444";                      // rose — Very high
}

export default function NISHistoryChart({ nisByMeal, nisDaily, lang = "bg", isDark = false }) {
  const { mode: dashMode } = useDashboardMode();  // iter566
  const labels = lang === "bg" ? MEAL_LABEL_BG : MEAL_LABEL_EN;
  const points = useMemo(() => {
    const items = [];
    MEAL_ORDER.forEach((mt) => {
      const block = nisByMeal?.[mt];
      if (block && typeof block.percent === "number") {
        items.push({
          meal_type: mt,
          label: labels[mt],
          percent: block.percent,
          tier_bg: block.tier_bg,
          tier_en: block.tier_en,
          color: block.color || tierColor(block.percent),
        });
      }
    });
    return items;
  }, [nisByMeal, labels]);

  if (points.length === 0) {
    return null;
  }

  // SVG geometry — single small line chart
  const W = 100, H = 60, PAD_X = 10, PAD_Y = 8;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;

  const xFor = (i) => points.length === 1
    ? W / 2
    : PAD_X + (i / (points.length - 1)) * innerW;
  const yFor = (p) => PAD_Y + innerH - (Math.min(100, Math.max(0, p)) / 100) * innerH;

  const linePath = points.map((p, i) =>
    `${i === 0 ? "M" : "L"} ${xFor(i).toFixed(2)} ${yFor(p.percent).toFixed(2)}`
  ).join(" ");

  // Area fill under the line
  const areaPath =
    `M ${PAD_X} ${H - PAD_Y}` +
    ` L ${xFor(0).toFixed(2)} ${yFor(points[0].percent).toFixed(2)}` +
    points.slice(1).map((p, i) =>
      ` L ${xFor(i + 1).toFixed(2)} ${yFor(p.percent).toFixed(2)}`
    ).join("") +
    ` L ${xFor(points.length - 1).toFixed(2)} ${H - PAD_Y} Z`;

  const peak = nisDaily?.peak_percent ?? Math.max(...points.map((p) => p.percent));
  const avg  = nisDaily?.avg_percent ?? Math.round(points.reduce((s, p) => s + p.percent, 0) / points.length);
  const peakColor = nisDaily?.peak_color || tierColor(peak);

  return (
    <motion.div
      data-testid="nis-history-chart"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`relative rounded-2xl ring-1 backdrop-blur-md shadow-sm ${
        isDark
          ? "bg-slate-900/60 ring-slate-800 text-slate-100"
          : "bg-white/95 ring-slate-200 text-slate-800"
      } p-4 sm:p-5`}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
            isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-600"
          }`}>
            <Activity className="w-4 h-4" strokeWidth={2} />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight" data-testid="nis-chart-title">
              {lang === "bg" ? "Инсулинов отговор през деня" : "Insulin response across the day"}
            </div>
            <div className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {/* iter566 — Лесен mode скрива технически „NIS %" + version */}
              {dashMode === "easy"
                ? (lang === "bg" ? "Инсулинов рейтинг по хранения" : "Insulin rating per meal")
                : (lang === "bg" ? "NIS % по хранения · v2.0-regression" : "NIS % per meal · v2.0-regression")}
            </div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1"
          style={{
            color: peakColor,
            background: `${peakColor}14`,
            borderColor: `${peakColor}40`,
          }}
          data-testid="nis-chart-peak-badge"
        >
          <TrendingUp className="w-3 h-3" />
          {lang === "bg" ? "Пик" : "Peak"} · {Math.round(peak)}%
        </span>
      </div>

      {/* ── Summary line ───────────────────────────────────────────── */}
      <div className={`text-xs mb-3 ${isDark ? "text-slate-300" : "text-slate-600"}`} data-testid="nis-chart-summary">
        {lang === "bg" ? (
          <>
            <span className="font-medium">Средно: <span style={{ color: tierColor(avg) }}>{avg}%</span></span>
            <span className="opacity-50 mx-1.5">·</span>
            <span>{points.length} хранения · най-висок пик: {labels[points.find((p) => p.percent === peak)?.meal_type] || "—"}</span>
          </>
        ) : (
          <>
            <span className="font-medium">Avg: <span style={{ color: tierColor(avg) }}>{avg}%</span></span>
            <span className="opacity-50 mx-1.5">·</span>
            <span>{points.length} meals · highest peak: {labels[points.find((p) => p.percent === peak)?.meal_type] || "—"}</span>
          </>
        )}
      </div>

      {/* ── SVG line chart ─────────────────────────────────────────── */}
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-32 sm:h-36">
          {/* gridlines at 25/50/75% */}
          {[25, 50, 75].map((g) => (
            <line
              key={g}
              x1={PAD_X} x2={W - PAD_X}
              y1={yFor(g)} y2={yFor(g)}
              stroke={isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.06)"}
              strokeWidth="0.4"
              strokeDasharray="0.6 1.2"
            />
          ))}
          {/* area fill */}
          <defs>
            <linearGradient id="nis-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={peakColor} stopOpacity="0.30" />
              <stop offset="100%" stopColor={peakColor} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#nis-area)" />
          {/* line */}
          <path
            d={linePath}
            stroke={peakColor}
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* dots */}
          {points.map((p, i) => (
            <g key={p.meal_type} data-testid={`nis-chart-dot-${p.meal_type}`}>
              <circle
                cx={xFor(i)} cy={yFor(p.percent)}
                r={p.percent === peak ? 2.4 : 1.8}
                fill={p.color}
                stroke={isDark ? "#0f172a" : "#fff"}
                strokeWidth="1"
              />
              {/* value label above dot */}
              <text
                x={xFor(i)}
                y={yFor(p.percent) - 3}
                fontSize="3.6"
                fontWeight="600"
                fill={isDark ? "#f1f5f9" : "#0f172a"}
                textAnchor="middle"
              >
                {Math.round(p.percent)}%
              </text>
            </g>
          ))}
        </svg>

        {/* x-axis labels */}
        <div className="flex justify-between mt-1 px-[6%]">
          {points.map((p) => (
            <span
              key={p.meal_type}
              className={`text-[10px] font-medium ${isDark ? "text-slate-400" : "text-slate-500"}`}
              data-testid={`nis-chart-label-${p.meal_type}`}
            >
              {p.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Disclaimer footer ──────────────────────────────────────── */}
      <div className={`mt-3 pt-3 border-t flex items-start gap-2 text-[10px] leading-tight ${
        isDark ? "border-slate-800 text-slate-500" : "border-slate-100 text-slate-500"
      }`}>
        <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <span>
          {lang === "bg"
            ? "Прогноза за пиковия инсулинов отговор за всяко хранене (не дневен сумарен товар). DIL остава отделен дневен метрик с таван."
            : "Predicted peak insulin response per meal (not a cumulative daily load). DIL is a separate daily metric with a ceiling."}
        </span>
      </div>
    </motion.div>
  );
}
